import './index.scss'

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAuth } from '@auth/AuthProvider'
import Modal from '@components/Modal'
import VolunteeringController from '@data/controller/VolunteeringController'
import { VolunteerTeamRole } from '@data/interfaces/Volunteering/volunteerteam.interface'
import useTrainingModules from '@hooks/useTrainingModules'
import { TrainingVolunteerAssignment } from '@interfaces/training.interface'
import RolePicker, { toggleRoleID } from '../RolePicker'

import closeIcon from 'url:@icon/close.svg';
import checkmarkIcon from 'url:@icon/checkmark.svg';

export type MemberRolesTarget = {
  personID: string;
  personName: string;
  teamID: string;
  teamName: string;
  roleIDs: string[];
};

export default function EditMemberRolesModal(props: {
  isOpen: boolean,
  target?: MemberRolesTarget | null,
  roles: VolunteerTeamRole[],
  close: () => void,
  onSaved: () => void,
  onManageRoles: () => void
}) {
  const auth = useAuth();
  const { t, i18n } = useTranslation(['editMemberRoles', 'volunteering']);
  const personName = props.target?.personName || t('fallbackPerson');

  // Only roles the team still defines count. One deleted since the page loaded
  // shouldn't read as a change, or be sent back and rejected.
  const knownRoleIDs = new Set(props.roles.map((role) => role.id));
  const initialRoleIDs = (props.target?.roleIDs ?? []).filter((roleID) => knownRoleIDs.has(roleID));

  const [roleIDs, setRoleIDs] = useState<string[]>(initialRoleIDs);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Training handed out by the roles just given, shown before closing so it
  // doesn't happen silently.
  const [assigned, setAssigned] = useState<TrainingVolunteerAssignment[] | null>(null);

  // Names for that list, fetched only if there's something to name.
  const { modules } = useTrainingModules({ enabled: (assigned?.length ?? 0) > 0 });
  const moduleNameByID = new Map(modules.map((module) => [module.id, module.attributes.name]));

  // Training is handed out for roles newly gained, so that's what the copy
  // counts -- not the number of modules.
  const gainedRoleCount = roleIDs.filter((roleID) => !initialRoleIDs.includes(roleID)).length;

  const hasChanges =
    roleIDs.length !== initialRoleIDs.length ||
    roleIDs.some((roleID) => !initialRoleIDs.includes(roleID));

  const save = async () => {
    if (!auth.churchId || !props.target) {
      setError(t('volunteering:errors.missingChurch'));
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const response = await VolunteeringController.setMemberRoles(
        auth.churchId,
        props.target.teamID,
        props.target.personID,
        roleIDs,
        auth
      );

      if (response.error) {
        setError(response.error);
        return;
      }

      const assignments = response.body?.autoAssignedTraining ?? [];

      if (assignments.length > 0) {
        setAssigned(assignments);
        return;
      }

      props.onSaved();
    } finally {
      setIsSaving(false);
    }
  };

  return <Modal
    id='edit-member-roles-modal'
    isOpen={props.isOpen}
    footerButtons={[]}>
    <div className='edit-member-roles-modal-content'>
      <div className='header'>
        <button
          type='button'
          className='header-control close'
          disabled={isSaving}
          // Once saved, closing is how the page learns the roles changed.
          onClick={assigned ? props.onSaved : props.close}>
          <img src={closeIcon} alt={t('volunteering:common.close')} />
        </button>

        <h3 className='title'>{assigned ? t('savedTitle') : t('title')}</h3>

        {assigned ? <span className='header-control-spacer' /> : <button
          type='button'
          className={isSaving ? 'header-control confirm saving' : 'header-control confirm'}
          disabled={!hasChanges || isSaving}
          onClick={() => {
            void save();
          }}>
          {isSaving ?
            <span className='header-control-spinner' role='status' aria-label={t('savingLabel')} /> :
            <img src={checkmarkIcon} alt={t('saveLabel')} />}
        </button>}
      </div>

      {assigned ? <div className='body'>
        <p className='assigned-training-copy'>
          {t('assigned', { person: personName, count: gainedRoleCount })}
        </p>

        <ul className='assigned-training'>
          {assigned.map((assignment) => {
            const dueDate = assignment.dueDate ? new Date(assignment.dueDate) : null;

            return <li key={assignment.moduleID}>
              <span className='assigned-training-name'>{moduleNameByID.get(assignment.moduleID) ?? t('fallbackModule')}</span>
              {dueDate && !Number.isNaN(dueDate.getTime()) ?
                <span className='assigned-training-due'>
                  {t('due', { date: dueDate.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' }) })}
                </span> :
                null}
            </li>
          })}
        </ul>

        <button type='button' className='text-button' onClick={props.onSaved}>{t('volunteering:common.done')}</button>
      </div> : <div className='body'>
        <p className='caption'>
          {props.target?.teamName ? t('caption', { person: personName, team: props.target.teamName }) : personName}
        </p>

        {error ? <p className='save-error'>{error}</p> : null}

        {props.roles.length === 0 ? <div className='no-roles'>
          <p>{t('noRoles')}</p>

          <button type='button' className='text-button' onClick={props.onManageRoles}>
            {t('createRoles')}
          </button>
        </div> : <RolePicker
          roles={props.roles}
          selectedRoleIDs={roleIDs}
          disabled={isSaving}
          onToggle={(roleID) => setRoleIDs((currentRoleIDs) => toggleRoleID(currentRoleIDs, roleID))}
        />}
      </div>}
    </div>
  </Modal>
}
